'use client';

/**
 * DemoWeightsCard — KlydeConfig retrieval source weights, self-attested.
 *
 * The capability that ran (typically `discovery_search`) returned a typed
 * `weights` block on its response: `config_sid` (KlydeConfig SID) +
 * per-source multipliers + operator rationale. This card lenses that
 * block. Self-suppresses when no scene carries a weights block — paint
 * is substrate-presence-driven, not case-shape-driven.
 *
 * Picks the latest scene whose `weights` is present (typically a fresh
 * discovery turn; refinement turns reuse the cached pool and don't
 * re-fire weights).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Scale } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TestRunView, WeightsBlock } from './demo-test-run-types';


export function DemoWeightsCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId || !data) return null;

  // Latest scene with a weights block — fresh turns fire weights;
  // refinement turns reuse the cached pool and leave them off.
  const sceneWithWeights = [...data.scenes].reverse().find((s) => s.weights);
  if (!sceneWithWeights || !sceneWithWeights.weights) return null;

  const w: WeightsBlock = sceneWithWeights.weights;
  const entries = Object.entries(w.weights);
  if (entries.length === 0) return null;

  const maxWeight = Math.max(...entries.map(([, v]) => v));

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-muted-foreground" />
          <div className={cn(T.body, 'text-sm font-medium')}>Retrieval source weights</div>
          <CognitionBadge mode="fast" />
        </div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs font-mono')}>
          {w.config_sid}
        </div>
      </div>
      <p className={cn(T.metadata, 'text-muted-foreground text-xs mb-3')}>
        Three retrieval sources contributed candidates. Each weighted before fusion.
      </p>

      <div className="space-y-2 mb-3">
        {entries.map(([name, multiplier]) => (
          <div key={name}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className={cn(T.body, 'font-medium capitalize')}>{name}</span>
              <span className={cn(T.metadata, 'font-mono font-medium text-sky-700 dark:text-sky-400')}>
                × {multiplier.toFixed(2).replace(/\.00$/, '')}
              </span>
            </div>
            <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-500/80 rounded-full"
                style={{ width: `${(multiplier / maxWeight) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {w.rationale && (
        <div className="bg-muted/30 rounded-lg px-3 py-2.5">
          <div className={cn(T.metadata, 'text-muted-foreground text-[10px] uppercase tracking-wide mb-1')}>
            Rationale
          </div>
          <p className={cn(T.body, 'text-xs text-muted-foreground leading-relaxed italic')}>
            {w.rationale}
          </p>
        </div>
      )}
    </div>
  );
}


function CognitionBadge({ mode }: { mode: 'llm' | 'fast' }) {
  const styles = mode === 'llm'
    ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return (
    <span className={cn('font-mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider', styles)}>
      cognition: {mode}
    </span>
  );
}
